import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import MathRenderer from '../components/MathRenderer';
import { SkeletonChat } from '../components/Skeletons';
import { Send, Camera, Mic, Trash2, Languages } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const STARTER_PROMPTS = [
  "Explain Rational Numbers to me",
  "I don't understand Linear Equations",
  "Why is negative × negative positive?",
  "Help me with my homework"
];

export default function AskZeno() {
  const { token, API, subscription } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [loading, setLoading] = useState(true);
  const [remaining, setRemaining] = useState(null);
  const [lang, setLang] = useState('en');
  const bottomRef = useRef(null);
  const plan = subscription?.plan || 'free';

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await axios.get(`${API}/zeno/history`, {
          headers: { Authorization: `Bearer ${token}` }, withCredentials: true
        });
        setMessages(res.data.messages || []);
      } catch { /* ignore */ } finally { setLoading(false); }
    };
    fetchHistory();
  }, [API, token]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typing]);

  const sendMessage = async (text) => {
    if (!text.trim()) return;
    const userMsg = { role: 'user', content: text, id: Date.now() };
    setMessages(p => [...p, userMsg]);
    setInput('');
    setTyping(true);
    try {
      const res = await axios.post(`${API}/zeno/chat`, { message: text, language: lang }, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true
      });
      setMessages(p => [...p, { role: 'assistant', content: res.data.response, id: Date.now() + 1 }]);
      if (res.data.remaining_free !== null && res.data.remaining_free !== undefined) {
        setRemaining(res.data.remaining_free);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Zeno is taking a nap. Try again!';
      if (err.response?.status === 403) {
        setMessages(p => [...p, { role: 'assistant', content: detail, id: Date.now() + 1 }]);
        setRemaining(0);
      } else {
        toast.error(detail);
      }
    } finally { setTyping(false); }
  };

  const clearHistory = async () => {
    try {
      await axios.delete(`${API}/zeno/history`, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true
      });
      setMessages([]);
      setRemaining(null);
      toast.success('Chat history cleared');
    } catch { toast.error('Failed to clear history'); }
  };

  const isBlocked = remaining === 0;

  return (
    <div className="min-h-screen flex flex-col pb-safe" style={{ background: '#FFF8F0' }}>
      {/* Header */}
      <div className="px-4 pt-4 pb-3 flex items-center justify-between border-b-2" style={{ borderColor: '#E5DACE' }}>
        <div className="flex items-center gap-3">
          <span className="text-3xl">🦉</span>
          <div>
            <p className="text-lg font-extrabold" style={{ color: '#1B4F8A' }}>Ask Zeno</p>
            <p className="text-xs font-semibold" style={{ color: '#06D6A0' }}>
              AI Math Tutor
              {plan === 'free' && remaining !== null && remaining > 0 && (
                <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: '#FFD23F30', color: '#FF6B35' }}>
                  {remaining} free left
                </span>
              )}
              {plan === 'free' && remaining === 0 && (
                <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: '#EF444420', color: '#EF4444' }}>
                  Limit reached
                </span>
              )}
              {plan !== 'free' && (
                <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: '#06D6A020', color: '#06D6A0' }}>
                  Premium
                </span>
              )}
            </p>
          </div>
        </div>
        {messages.length > 0 && (
          <button data-testid="clear-chat-btn" onClick={clearHistory} className="p-2 rounded-full" style={{ color: '#8B7355' }}>
            <Trash2 size={18} />
          </button>
        )}
        <button data-testid="lang-toggle-btn" onClick={() => setLang(l => l === 'en' ? 'hi' : 'en')}
          className="flex items-center gap-1 px-2 py-1 rounded-full border-2 text-xs font-bold transition-all"
          style={{ borderColor: lang === 'hi' ? '#FF6B35' : '#E5DACE', color: lang === 'hi' ? '#FF6B35' : '#8B7355', background: lang === 'hi' ? '#FFF0E5' : 'white' }}>
          <Languages size={14} />
          {lang === 'hi' ? 'हिंदी' : 'EN'}
        </button>
      </div>

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-area">
        {loading ? (
          <SkeletonChat />
        ) : messages.length === 0 ? (
          <div className="text-center py-8">
            <span className="text-6xl block mb-4">🦉</span>
            <p className="text-lg font-extrabold mb-2" style={{ color: '#1B4F8A' }}>Hi! I'm Zeno!</p>
            <p className="text-sm font-semibold mb-6" style={{ color: '#8B7355' }}>
              {plan === 'free' ? 'You get 5 free questions! Ask me anything about math.' : 'Ask me any math question!'}
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {STARTER_PROMPTS.map((p, i) => (
                <button key={i} data-testid={`starter-prompt-${i}`} onClick={() => sendMessage(p)}
                  className="btn-press bg-white border-2 rounded-full px-4 py-2 text-xs font-bold"
                  style={{ borderColor: '#E5DACE', color: '#1B4F8A', boxShadow: '0 2px 0 #E5DACE' }}>
                  {p}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m, i) => (
            <div key={m.id || i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {m.role === 'assistant' && <span className="text-2xl mr-2 shrink-0 mt-1">🦉</span>}
              <div className={`max-w-[80%] px-4 py-3 text-sm font-semibold ${m.role === 'user' ? 'chat-bubble-student' : 'chat-bubble-zeno'}`}
                style={{
                  background: m.role === 'user' ? '#FF6B35' : 'white',
                  color: m.role === 'user' ? 'white' : '#1B4F8A',
                  border: m.role === 'assistant' ? '2px solid #E5DACE' : 'none'
                }}>
                <div className="whitespace-pre-wrap">
                  <MathRenderer text={m.content} />
                </div>
              </div>
            </div>
          ))
        )}
        {typing && (
          <div className="flex items-center gap-2">
            <span className="text-2xl">🦉</span>
            <div className="bg-white border-2 rounded-2xl px-4 py-3" style={{ borderColor: '#E5DACE' }}>
              <div className="flex gap-1">
                <span className="w-2 h-2 rounded-full animate-bounce" style={{ background: '#FF6B35', animationDelay: '0ms' }} />
                <span className="w-2 h-2 rounded-full animate-bounce" style={{ background: '#FF6B35', animationDelay: '200ms' }} />
                <span className="w-2 h-2 rounded-full animate-bounce" style={{ background: '#FF6B35', animationDelay: '400ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input Area */}
      <div className="px-4 pb-4 pt-2 border-t-2" style={{ borderColor: '#E5DACE', background: '#FFF8F0' }}>
        <form onSubmit={e => { e.preventDefault(); sendMessage(input); }} className="flex items-center gap-2">
          <button type="button" onClick={() => window.location.href='/camera-doubt'} className="p-2" style={{ color: '#8B7355' }}>
            <Camera size={20} />
          </button>
          <button type="button" onClick={() => toast.info('Use the Snap & Solve page for voice input')} className="p-2" style={{ color: '#8B7355' }}>
            <Mic size={20} />
          </button>
          <input data-testid="zeno-input" value={input} onChange={e => setInput(e.target.value)}
            placeholder={isBlocked ? "Upgrade for unlimited Zeno" : "Ask me anything about math..."}
            disabled={isBlocked}
            className="flex-1 bg-white border-2 rounded-full px-4 py-3 text-sm font-bold outline-none focus:border-[#FF6B35] transition-all"
            style={{ borderColor: '#E5DACE', color: '#1B4F8A' }} />
          <button data-testid="zeno-send-btn" type="submit" disabled={!input.trim() || typing || isBlocked}
            className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
            style={{ background: input.trim() ? '#FF6B35' : '#E5DACE' }}>
            <Send size={18} color="white" />
          </button>
        </form>
      </div>

      <BottomNav active="zeno" />
    </div>
  );
}
